import Chip from '@mui/material/Chip';
import {styled} from '@mui/material/styles';
import {useQuery} from '@tanstack/react-query';
import {format} from 'date-fns';
import React, {useEffect, useState} from 'react';
import ReactJson from 'react-json-view';
import {useSelector} from 'react-redux';

import CustomSnackbar from '../../components/CustomSnackBar/CustomSnackBar';
import CustomTable from '../../components/CustomTable/CustomTable';
import FilterBar from '../../components/FilterBar/FilterBar';
import ToolBar from '../../components/ToolBar/ToolBar';
import {ConfigConstant} from '../../constants/config';
import {getRawData} from '../../service/OpsRaw';
import {pxToRem} from '../../utils';
import {formatChannelName, getDateFromText, getDefaultFilters} from '../../utils/helper';

import OperationRawDrawer from './OperationRawDrawer';

export const StyledChip = styled(Chip)(({theme, label}) => ({
  fontWeight: 800,
  width: pxToRem(80),
  color:
    label === ConfigConstant.MONGO_CONFIG?.STATUSES?.SUCCESS
      ? theme.palette.success.dark
      : label === ConfigConstant.MONGO_CONFIG?.STATUSES?.FAILED
        ? theme.palette.error.dark
        : theme.palette.warning.dark,
  backgroundColor:
    label === ConfigConstant.MONGO_CONFIG?.STATUSES?.SUCCESS
      ? theme.palette.success.light3
      : label === ConfigConstant.MONGO_CONFIG?.STATUSES?.FAILED
        ? theme.palette.error.light3
        : theme.palette.warning.light3,
  '&:hover': {
    backgroundColor:
      label === ConfigConstant.MONGO_CONFIG?.STATUSES?.SUCCESS
        ? theme.palette.success.light2
        : label === ConfigConstant.MONGO_CONFIG?.STATUSES?.FAILED
          ? theme.palette.error.light2
          : theme.palette.warning.light2,
  },
}));

const OperationRaw = () => {
  const [snackbarAlert, setSnackBarAlert] = useState('');
  const closeSnackBarAlert = () => {
    setSnackBarAlert('');
  };

  const commonFilters = useSelector((store) => store.commonFilters);

  const defaultFilters = getDefaultFilters(window?.location?.search, commonFilters);

  const [openDrawer, setOpenDrawer] = useState(false);

  const [selectedRow, setSelectedRow] = useState({index: null, row: null});

  const [filters, setFilters] = useState([
    {
      id: 'Date',
      displayValue: defaultFilters?.date?.displayValue || 'Today',
      value: defaultFilters?.date?.value || getDateFromText('Today'),
      type: 'date',
      selectType: 'single',
      key: 'date',
      isApplied: true,
      isDeleteable: false,
    },
    {
      id: 'Operation',
      type: 'menu',
      selectType: 'multiple',
      key: 'operationType',
      displayValue: defaultFilters?.operationType?.displayValue || ['Menu Push'],
      value: defaultFilters?.operationType?.value || ['MENU_PUSH'],
      isApplied: true,
      isDeleteable: false,
    },
    {
      id: 'Correlation Id',
      type: 'text',
      key: 'rebelCorrelationId',
      displayValue: defaultFilters?.rebelCorrelationId?.displayValue || '',
      value: defaultFilters?.rebelCorrelationId?.value || '',
      isApplied: defaultFilters?.rebelCorrelationId?.value ? true : false,
      isDeleteable: true,
    },
    {
      id: 'Bulk Toggle Batch Id',
      type: 'text',
      key: 'bulkToggleBatchId',
      displayValue: defaultFilters?.bulkToggleBatchId?.displayValue || '',
      value: defaultFilters?.bulkToggleBatchId?.value || '',
      isApplied: defaultFilters?.bulkToggleBatchId?.value ? true : false,
      isDeleteable: true,
    },
    {
      id: 'City',
      type: 'menu',
      selectType: 'multiple',
      key: 'city',
      displayValue: defaultFilters?.city?.displayValue || [],
      value: defaultFilters?.city?.value || [],
      isApplied: Array.isArray(defaultFilters?.city?.value) && defaultFilters?.city?.value.length > 0 ? true : false,
      isDeleteable: true,
    },
    {
      id: 'Country',
      type: 'menu',
      selectType: 'multiple',
      key: 'countryCode',
      displayValue: defaultFilters?.countryCode?.displayValue || [],
      value: defaultFilters?.countryCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.countryCode?.value) && defaultFilters?.countryCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Store',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerPhyStoreCode',
      displayValue: defaultFilters?.partnerPhyStoreCode?.displayValue || [],
      value: defaultFilters?.partnerPhyStoreCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerPhyStoreCode?.value) &&
        defaultFilters?.partnerPhyStoreCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Channel',
      type: 'menu',
      selectType: 'multiple',
      key: 'channelCode',
      displayValue: defaultFilters?.channelCode?.displayValue || [],
      value: defaultFilters?.channelCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.channelCode?.value) && defaultFilters?.channelCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Brand',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerBrandCode',
      displayValue: defaultFilters?.partnerBrandCode?.displayValue || [],
      value: defaultFilters?.partnerBrandCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerBrandCode?.value) && defaultFilters?.partnerBrandCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Virtual Store Code',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerStoreCode',
      displayValue: defaultFilters?.partnerStoreCode?.displayValue || [],
      value: defaultFilters?.partnerStoreCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerStoreCode?.value) && defaultFilters?.partnerStoreCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Status',
      type: 'menu',
      selectType: 'single',
      key: 'status',
      displayValue: defaultFilters?.status?.displayValue || null,
      value: defaultFilters?.status?.value || null,
      isApplied: defaultFilters?.status?.value ? true : false,
      isDeleteable: true,
    },
    {
      id: 'Toggle Type',
      type: 'menu',
      selectType: 'single',
      key: 'toggleType',
      displayValue: defaultFilters?.toggleType?.displayValue || null,
      value: defaultFilters?.toggleType?.value || null,
      isApplied: defaultFilters?.toggleType?.value ? true : false,
      isDeleteable: true,
    },
    {
      id: 'Store Toggle Type',
      type: 'menu',
      selectType: 'single',
      key: 'storeToggleType',
      displayValue: defaultFilters?.storeToggleType?.displayValue || null,
      value: defaultFilters?.storeToggleType?.value || null,
      isApplied: defaultFilters?.storeToggleType?.value ? true : false,
      isDeleteable: true,
    },
    {
      id: 'Request Client',
      type: 'menu',
      selectType: 'single',
      key: 'requestClient',
      displayValue: defaultFilters?.requestClient?.displayValue || null,
      value: defaultFilters?.requestClient?.value || null,
      isApplied: defaultFilters?.requestClient?.value ? true : false,
      isDeleteable: true,
      isClientFilter: false,
    },
  ]);

  const [columnVisibility, setColumnVisibility] = React.useState({
    'Product Ids': false,
    'Toggle Type': false,
  });

  const changeColumnVisibility = () => {
    filters.forEach((filter) => {
      if (filter?.id === 'Operation') {
        const isProductToggleSelected = filter?.value?.includes('PRODUCT_TOGGLE');
        if (filter?.value?.every((ele) => ele === 'MENU_PUSH')) {
          setColumnVisibility((prevState) => ({
            ...prevState,
            'Product Ids': false,
            'Toggle Type': false,
          }));
        } else if (isProductToggleSelected || filter?.value?.includes('STORE_TOGGLE')) {
          setColumnVisibility((prevState) => ({
            ...prevState,
            'Product Ids': isProductToggleSelected,
            'Toggle Type': true,
          }));
        }
      }
    });
  };

  const [{pageIndex, pageSize}, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const pageSizeOptions = [50, 100, 200];
  const dataQuery = useQuery({
    queryKey: ['data-opsRaw', {pageIndex, pageSize}],
    queryFn: () => getRawData({pageIndex, pageSize, filters}),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleDrawerOpen = (index, row) => {
    setOpenDrawer(true);
    setSelectedRow({index, row});
  };

  const handleDrawerClose = () => {
    setOpenDrawer(false);
    setSelectedRow({index: null, row: null});
  };

  const columns = [
    {
      id: 'Date',
      header: 'Date',
      filterable: true,
      accessorFn: (row) =>
        `${format(new Date(row.timestamp), 'dd MMM')} ${format(new Date(row.timestamp), 'HH:mm:ss')}`,
    },
    {
      id: 'Operation',
      header: 'Operation',
      accessorFn: (row) => ConfigConstant.MONGO_CONFIG.OPERATION_TYPES[row?.operationType]?.label,
      filterable: true,
    },
    {
      id: 'City',
      header: 'City',
      accessorKey: 'city',
      filterable: true,
    },
    {
      id: 'Store',
      header: 'Store',
      accessorKey: 'partnerPhyStoreName',
      filterable: true,
    },
    {
      id: 'Channel',
      header: 'Channel',
      filterable: true,
      accessorFn: (row) => formatChannelName(row),
    },
    {
      id: 'Brand',
      header: 'Brand',
      accessorKey: 'partnerBrandName',
      filterable: true,
    },
    {
      id: 'Virtual Store Code',
      header: 'Virtual Store Code',
      accessorFn: (row) =>
        `${row?.partnerStoreCode} ${row?.storeToggleType === 'HYBRID' ? `(${row.storeToggleType})` : ``}`,
      filterable: true,
    },
    {
      id: 'Product Ids',
      header: 'Product Id(s)',
      accessorKey: 'itemIds',
    },
    {
      id: 'Toggle Type',
      header: 'Toggle Type',
      accessorKey: 'toggleType',
      filterable: true,
    },
    {
      id: 'Status',
      header: 'Status',
      filterable: true,
      cell: ({row}) => {
        return <StyledChip onClick={() => handleDrawerOpen(row.index, row?.original)} label={row.original?.status} />;
      },
    },
    {
      id: 'Message',
      header: 'Message',
      cell: ({row}) => {
        if (
          ConfigConstant.MONGO_CONFIG?.OPERATION_TYPES?.MENU_PUSH?.code === row?.original?.operationType &&
          ConfigConstant.MONGO_CONFIG?.CHANNEL_SUPPORT_ASYNC_MENU_PROCESSING.includes(
            row?.original?.channelGroupCode
          ) &&
          ConfigConstant.MONGO_CONFIG?.STATUSES?.QUEUED !== row?.original?.status &&
          row?.original?.processedResponse
        ) {
          return (
            <ReactJson
              src={row?.original?.processedResponse?.data}
              name={false}
              displayDataTypes={false}
              collapsed={true}
            />
          );
        }
        return row?.original?.response?.data ? JSON.stringify(row?.original?.response?.data) : '-';
      },
    },
  ];

  useEffect(() => {
    changeColumnVisibility();
  }, []);

  const getDataToExport = () => {
    if (Array.isArray(dataQuery.data?.rows) && dataQuery.data.rows.length > 0) {
      const dateFilter = filters.find((filter) => filter.id === 'Date');
      const fileName = `operationRaw(${format(new Date(dateFilter.value[0]), 'dd-MM-yyyy')}-${format(
        new Date(dateFilter.value[1]),
        'dd-MM-yyyy'
      )})`;
      const data = {
        fileName,
        dataToExport: `Operation Type, Correlation Id, Date, City, Country, Store Code, Store, Channel Code, Channel, Brand Code, Brand, Virtual Store Code, Channel Store Code, Status, Message`,
      };
      dataQuery.data?.rows?.forEach(
        ({
          operationType,
          rebelCorrelationId,
          timestamp,
          city,
          countryName,
          partnerPhyStoreCode,
          partnerPhyStoreName,
          channelCode,
          channelGroupCode,
          channelName,
          channelGroupName,
          partnerBrandCode,
          partnerBrandName,
          partnerStoreCode,
          channelStoreCode,
          status,
          response,
          processedResponse,
        }) => {
          let message = '';
          if (
            operationType === ConfigConstant.MONGO_CONFIG?.OPERATION_TYPES.MENU_PUSH.code &&
            ConfigConstant.MONGO_CONFIG?.CHANNEL_SUPPORT_ASYNC_MENU_PROCESSING?.includes(channelGroupCode) &&
            status !== ConfigConstant.MONGO_CONFIG?.STATUSES?.QUEUED
          ) {
            message = processedResponse
              ? `"${JSON.stringify(processedResponse)?.replace(/"/g, '""')}"`
              : `"${JSON.stringify(response)?.replace(/"/g, '""')}"`;
          } else {
            message = `"${JSON.stringify(response)?.replace(/"/g, '""')}"`;
          }
          data.dataToExport += `\n${operationType},${rebelCorrelationId},${
            timestamp ? format(new Date(timestamp), 'dd MMM HH:mm:ss') : '-'
          },${city},${countryName},${partnerPhyStoreCode},${partnerPhyStoreName},${channelCode},${
            channelName !== channelGroupName ? `${channelGroupName} - ${channelName}` : channelName
          },${partnerBrandCode},${partnerBrandName},${partnerStoreCode},${channelStoreCode},${status},${message}`;
        }
      );
      return data;
    } else {
      return null;
    }
  };

  return (
    <>
      <ToolBar
        headerText={'Operations Raw'}
        showGlobalSearch={false}
        showLegends={false}
        showExportButton={{button: true, tooltip: true}}
        getDataToExport={getDataToExport}
      />
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        showApplyActionButton={true}
        showFilterActionButton={true}
        dataQuery={[dataQuery]}
        allowDateRangeSelection={false}
        setPagination={setPagination}
        setSnackBarAlert={setSnackBarAlert}
        shouldValidateMultiOpsSelection={true}
        changeColumnVisibility={changeColumnVisibility}
      />
      <CustomTable
        tableKey={'ops-raw'}
        columns={columns}
        dataQuery={dataQuery}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        pageIndex={pageIndex}
        setPagination={setPagination}
        filters={filters}
        setFilters={setFilters}
        columnVisibility={columnVisibility}
        allowDateRangeSelection={false}
      />
      {selectedRow.index > -1 && selectedRow.row && (
        <OperationRawDrawer
          open={openDrawer}
          onToggleOff={handleDrawerClose}
          selectedRow={selectedRow}
          dataQuery={dataQuery}
          filters={filters}
        />
      )}
      {snackbarAlert && (
        <CustomSnackbar
          autoHideDuration={3000}
          message={snackbarAlert}
          isOpen={Boolean(snackbarAlert)}
          showCloseButton={true}
          handleClose={closeSnackBarAlert}
          isAlert={true}
          severity={'warning'}
          anchorOriginVertical={'top'}
        />
      )}
    </>
  );
};

export default OperationRaw;
